import React from "react";
import { Toast } from "../../../components/common/Toast";
import { AddressInfoIcon, BusinessInformationIcon, SavingIcon, SocialInfoIcon } from "../../../components/common/SvgIcon";
import SectionCard from "../../../components/common/SectionCard";
import FormField from "../../../components/common/FormField";
import ConfirmationModal from "../../../components/common/ConfirmationModal";

const save = `${LEGAL_PAGES.assets_url}/images/icons/save.png`;
const reset = `${LEGAL_PAGES.assets_url}/images/icons/reset.png`;
const deleteIcon = `${LEGAL_PAGES.assets_url}/images/icons/delete.png`;

const GeneralInformation = ({ formData, handleInputChange, saving, handleSave, handleReset, isLoading, setIsLoading, handleResetSavedData, showDeleteModal, setShowDeleteModal }) => {

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      Toast.success("Placeholder copied to clipboard!");
    } catch (err) {
      console.error("Fallback copy failed:", err);
      Toast.error("Failed to copy to clipboard");
    }
    document.body.removeChild(textArea);
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => Toast.success("Placeholder copied to clipboard!"))
        .catch((err) => {
          console.error("Failed to copy:", err);
          fallbackCopy(text);
        });
    } else {
      fallbackCopy(text);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave();
  };

  const handleResetClick = (e) => {
    e.preventDefault();
    handleReset();
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    setShowDeleteModal(true);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="space-y-6 mt-6">
        {/* Business Information */}
        {isLoading ? (
          <SectionCard title="Business Information" icon={<BusinessInformationIcon/>} maxWidth="max-w-[1150px]">
            <div className="space-y-3">
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </SectionCard>
        ) : (
          <SectionCard
            title="Business Information"
            icon={<BusinessInformationIcon />}
            maxWidth="max-w-[1150px]"
            maxHeight="max-h-[540px]"
          >

            <div className="space-y-3">
              <FormField
                label="Your Site URL"
                name="site_url"
                placeholder="[siteUrl]"
                value={formData.site_url}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="Site / Business Name"
                name="site_name"
                placeholder="[siteName]"
                value={formData.site_name}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="Business Niche"
                name="business_niche"
                placeholder="[businessNiche]"
                value={formData.business_niche}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="Phone Number"
                name="phone_number"
                placeholder="[phoneNumber]"
                value={formData.phone_number}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
                type="tel"
              />
              <FormField
                label="Email Address"
                name="email_address"
                placeholder="[emailAddress]"
                value={formData.email_address}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
            </div>
          </SectionCard>
        )}

        {/* Address Information */}
        {isLoading ? (
          <SectionCard title="Address Information" icon={<AddressInfoIcon />}>
            <div className="space-y-3">
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </SectionCard>
        ) : (
          <SectionCard
            maxHeight="max-h-[670px]"
            title="Address Information"
            icon={<AddressInfoIcon />}
          >
            <div className="space-y-3">
              <FormField
                label="Street Name"
                name="street_name"
                placeholder="[streetName]"
                value={formData.street_name}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="City"
                name="city"
                placeholder="[cityName]"
                value={formData.city}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="State"
                name="state"
                placeholder="[stateName]"
                value={formData.state}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="Country Name"
                name="country_name"
                placeholder="[countryName]"
                value={formData.country_name}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="Zip/Postal Code"
                name="zip_code"
                placeholder="[zipCode]"
                value={formData.zip_code}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="Complete Mailing Address"
                name="complete_address"
                placeholder="[mailingAddress]"
                value={formData.complete_address}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
            </div>
          </SectionCard>
        )}

        {/* Social Information */}
        {isLoading ? (
          <SectionCard title="Social Information" icon={<SocialInfoIcon />}>
            <div className="space-y-3">
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </SectionCard>
        ) : (
          <SectionCard
            title="Social Information"
            icon={<SocialInfoIcon />}
          >
            <div className="space-y-3">
              <FormField
                label="Facebook Page URL"
                name="facebook_url"
                placeholder="[facebookUrl]"
                value={formData.facebook_url}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="Google Plus URL"
                name="google_plus_url"
                placeholder="[googlePlusUrl]"
                value={formData.google_plus_url}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="LinkedIn Profile URL"
                name="linkedin_url"
                placeholder="[linkedInUrl]"
                value={formData.linkedin_url}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
              <FormField
                label="Twitter URL"
                name="twitter_url"
                placeholder="[twitterUrl]"
                value={formData.twitter_url}
                onChange={handleInputChange}
                onCopy={copyToClipboard}
              />
            </div>
          </SectionCard>
        )}

        {/* Action Bar */}
        <div className="flex items-center justify-start gap-2 rounded-xl p-4">
          <button
            type="submit"
            disabled={saving}
            className={[
              "inline-flex items-center justify-center rounded-xl px-6 py-[14px] text-sm font-semibold text-white shadow-[0px_4px_14px_-2px_#7C3BED40] transition",
              saving ? "cursor-not-allowed bg-indigo-400" : "bg-[#7C3BED] hover:bg-indigo-700",
            ].join(" ")}
          >
            <img src={save} alt="Save Icon" className="mr-2 h-4 w-4" />
            {saving ? (
              <span className="flex items-center gap-2">
                <SavingIcon />
                Saving...
              </span>
            ) : (
              "Save Changes"
            )}
          </button>

            <button
              type="button"
              onClick={handleResetClick}
              className="inline-flex items-center justify-center rounded-xl border border-[#E4E4E7] bg-white px-6 py-[14px] text-sm font-semibold text-[#17171C] transition hover:bg-gray-50"
            >
              <img src={reset} alt="Reset Icon" className="mr-2 h-4 w-4" />
              Reset Fields
            </button>
            <button
              type="button"
              onClick={handleDeleteClick}
              className="inline-flex items-center justify-center rounded-xl border border-[#E4E4E7] bg-white px-6 py-[14px] text-sm font-semibold text-[#EF4343]"
            >
              <img src={deleteIcon} alt="Reset Data Icon" className="mr-2 h-4 w-4" />
              Reset Saved Data
            </button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleResetSavedData}
        title="Confirm Delete"
        message="This will permanently delete all saved data. Are you sure you want to proceed?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </form>
  );
};

export default GeneralInformation;